import * as React from 'react'
import { useState } from 'react'
import TimeFields from '../resources_records/TimeFields'
import MachineFields from '../resources_records/MachineFields'
import ItemFields from '../resources_records/ItemFields'
import DependentTaskPicker from '../resources_records/DependentTaskPicker'
import NestedFieldset from '../resources_records/NestedFieldset'
import { loadPackagingsItemOptions } from '../../api/items'
import useNestedAttributes from '../../hooks/useNestedAttributes'
import useResourcesRecordFormController from '../../hooks/useResourcesRecordFormController'
import RecordTime from './RecordTime'
import RecordMachine from './RecordMachine'
import RecordItem from './RecordItem'
import RecordPackaging from './RecordPackaging'
import useRecordAdjustment from './useRecordAdjustment'
import { ResourcesAdjustmentShape } from '../../api/api_data_types'
import OrderNumberPicker from "../shared/order_number_picker/OrderNumberPicker";

type AdjustmentsFormFieldsProps = {
  adjustment: ResourcesAdjustmentShape
  availableWorktypes: any[]
  availableMachines: any[]
  availableWarehouses: any[]
  hideMachines?: boolean
  hideItems?: boolean
  hidePackagings?: boolean
}

function AdjustmentsFormFields({
  adjustment,
  availableWorktypes,
  availableMachines,
  availableWarehouses,
  hideMachines = false,
  hideItems = false,
  hidePackagings = false,
}: AdjustmentsFormFieldsProps) {
  const { jobnumber, date, record } = adjustment
  const { formData, setFormData } = useResourcesRecordFormController(adjustment)

  const [times, noVisibleTimes, addTime, removeTime, setTime] = useNestedAttributes(formData, setFormData, 'times_attributes')
  const [hasRecordTimes, addRecordTimeAdjustment] = useRecordAdjustment(record?.times || [], addTime, 'time')

  const [machines, noVisibleMachines, addMachine, removeMachine, setMachine] = useNestedAttributes(
    formData,
    setFormData,
    'machines_attributes'
  )
  const [hasRecordMachines, addRecordMachineAdjustment] = useRecordAdjustment(record?.machines || [], addMachine, 'time')

  const [items, noVisibleItems, addItem, removeItem, setItem] = useNestedAttributes(formData, setFormData, 'items_attributes')
  const [hasRecordItems, addRecordItemAdjustment] = useRecordAdjustment(record?.items || [], addItem, 'amount')

  const [packagings, noVisiblePackagings, addPackaging, removePackaging, setPackaging] = useNestedAttributes(
    formData,
    setFormData,
    'packagings_attributes'
  )
  const [hasRecordPackagings, addRecordPackagingAdjustment] = useRecordAdjustment(record?.packagings || [], addPackaging, 'amount')

  const [orderNumber, setOrderNumber] = useState(null)
  const [taskId, setTaskId] = useState(null)

  return (
    <>
      <div className="form-group">
        {jobnumber && (
          <>
            <label htmlFor="resources-adjustment-jobnumber-id">Ordrenummer, Sags-ID eller beskrivelse</label>
            <select className="form-control" id="resources-adjustment-jobnumber-id" disabled defaultValue={jobnumber?.full_title}>
              <option>{jobnumber?.full_title}</option>
            </select>
            <input type="hidden" name="resources_adjustment[jobnumber_id]" value={jobnumber.id} />
          </>
        )}
        {!jobnumber && (
          <>
            <OrderNumberPicker orderNumber={orderNumber} setOrderNumber={setOrderNumber} />
            <input type="hidden" name="resources_adjustment[jobnumber_id]" value={orderNumber?.id || ''} />
            <DependentTaskPicker
              jobnumber={orderNumber}
              taskId={taskId}
              setTaskId={id => setTaskId(id)}
              name="resources_adjustment[task_id]"
            />
          </>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="resources-record-date">Dato</label>
        <input id="resources-record-date" required disabled type="date" className="form-control col-xs-12" defaultValue={date} />
        <input type="hidden" name="resources_adjustment[date]" value={date} />
      </div>

      <NestedFieldset className="mb-4 adjustment-times" title="Timer" addButtonTitle="+ Tilføj" onAddButtonClick={addTime}>
        {noVisibleTimes && !hasRecordTimes && <div className="text-muted text-center">Ingen timer</div>}
        {hasRecordTimes &&
          record.times.map(time => <RecordTime className="mb-2" key={`time-${time.id}`} time={time} onAdjust={addRecordTimeAdjustment} />)}
        {Object.keys(times).map((key, index) => {
          const time = times[key]
          return (
            <TimeFields
              className={`${index > 0 ? 'mt-2' : ''}`}
              key={key}
              idKey={key}
              fieldNameFn={field => `resources_adjustment[times_attributes][${index}][${field}]`}
              showSignControl={true}
              timeFields={time}
              setTimeFields={data => setTime(key, data)}
              handleRemove={() => removeTime(key)}
              availableWorktypes={availableWorktypes}
              allowRemove={true}
            />
          )
        })}
      </NestedFieldset>

      {!hideMachines && (
        <NestedFieldset className="mb-4 adjustment-machines" title="Maskiner" addButtonTitle="+ Tilføj" onAddButtonClick={addMachine}>
          {noVisibleMachines && !hasRecordMachines && <div className="text-muted text-center">Ingen maskiner</div>}
          {hasRecordMachines &&
            record.machines.map(machine => (
              <RecordMachine className="mb-2" key={`machine-${machine.id}`} machine={machine} onAdjust={addRecordMachineAdjustment} />
            ))}
          {Object.keys(machines).map((key, index) => {
            const machine = machines[key]
            return (
              <MachineFields
                className={`${index > 0 ? 'mt-2' : ''}`}
                key={key}
                idKey={key}
                machineFields={machine}
                fieldNameFn={field => `resources_adjustment[machines_attributes][${index}][${field}]`}
                setMachineFields={data => setMachine(key, data)}
                availableMachines={availableMachines}
                handleRemove={() => removeMachine(key)}
                showSignControl={true}
              />
            )
          })}
        </NestedFieldset>
      )}

      {!hideItems && (
        <NestedFieldset className="mb-4 adjustment-items" title="Materialer" addButtonTitle="+ Tilføj" onAddButtonClick={addItem}>
          {noVisibleItems && !hasRecordItems && <div className="text-muted text-center">Ingen materialer</div>}
          {hasRecordItems &&
            record.items.map(item => (
              <RecordItem className="mb-2" key={`item-${item.id}`} item={item} onAdjust={addRecordItemAdjustment} />
            ))}
          {Object.keys(items).map((key, index) => {
            const item = items[key]
            return (
              <ItemFields
                className={`${index > 0 ? 'mt-2' : ''}`}
                key={key}
                idKey={key}
                itemFields={item}
                fieldNameFn={field => `resources_adjustment[items_attributes][${index}][${field}]`}
                setItemFields={data => setItem(key, data)}
                handleRemove={() => removeItem(key)}
                availableWarehouses={availableWarehouses}
                showSignControl={true}
              />
            )
          })}
        </NestedFieldset>
      )}

      {!hidePackagings && (
        <NestedFieldset className="mb-4 adjustment-packagings" title="Emballage & Diverse" addButtonTitle="+ Tilføj" onAddButtonClick={addPackaging}>
          {noVisiblePackagings && !hasRecordPackagings && <div className="text-muted text-center">Ingen emballage</div>}
          {hasRecordPackagings &&
            record.packagings.map(packaging => (
              <RecordPackaging
                className="mb-2"
                key={`packaging-${packaging.id}`}
                packaging={packaging}
                onAdjust={addRecordPackagingAdjustment}
              />
            ))}
          {Object.keys(packagings).map((key, index) => {
            const packaging = packagings[key]
            return (
              <ItemFields
                key={key}
                idKey={key}
                itemFields={packaging}
                fieldNameFn={field => `resources_adjustment[packagings_attributes][${index}][${field}]`}
                setItemFields={data => setPackaging(key, data)}
                handleRemove={() => removePackaging(key)}
                availableWarehouses={availableWarehouses}
                loadItemOptions={loadPackagingsItemOptions}
                showSignControl={true}
              />
            )
          })}
        </NestedFieldset>
      )}
    </>
  )
}

export default AdjustmentsFormFields
